/**
 * Editor API Service - HTTP client for editor REST API
 *
 * Handles all HTTP requests to the editor endpoints
 */

import type {
  Category,
  WordBank,
  PromptFile,
  FrameworkFile,
  EditorDataOverview,
  EditorApiResponse,
} from '@blather/shared';

// In production (no VITE_SERVER_URL): use /api/editor (Caddy strips /api, server receives /editor)
// In development: use direct server URL with /editor
const API_BASE = import.meta.env.VITE_SERVER_URL
  ? `${import.meta.env.VITE_SERVER_URL}/editor`
  : '/api/editor';

/**
 * Editor API Service for CRUD operations
 */
export const editorApiService = {
  /**
   * Get overview of all editable data
   */
  async getOverview(): Promise<EditorDataOverview> {
    const response = await fetch(`${API_BASE}/data`);
    if (!response.ok) {
      throw new Error(`Failed to load data overview: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get a specific word bank
   */
  async getWordBank(slotType: string): Promise<WordBank> {
    const response = await fetch(`${API_BASE}/wordbanks/${slotType}`);
    if (!response.ok) {
      throw new Error(`Failed to load word bank: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Update a word bank
   */
  async updateWordBank(slotType: string, data: WordBank): Promise<EditorApiResponse<WordBank>> {
    const response = await fetch(`${API_BASE}/wordbanks/${slotType}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return result;
    }

    return result;
  },

  /**
   * Get prompts for a category
   */
  async getPrompts(category: Category): Promise<PromptFile> {
    const response = await fetch(`${API_BASE}/prompts/${category}`);
    if (!response.ok) {
      throw new Error(`Failed to load prompts: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Update prompts for a category
   */
  async updatePrompts(category: Category, data: PromptFile): Promise<EditorApiResponse<PromptFile>> {
    const response = await fetch(`${API_BASE}/prompts/${category}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return result;
    }

    return result;
  },

  /**
   * Get frameworks for a category
   */
  async getFrameworks(category: Category): Promise<FrameworkFile> {
    const response = await fetch(`${API_BASE}/frameworks/${category}`);
    if (!response.ok) {
      throw new Error(`Failed to load frameworks: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Update frameworks for a category
   */
  async updateFrameworks(category: Category, data: FrameworkFile): Promise<EditorApiResponse<FrameworkFile>> {
    const response = await fetch(`${API_BASE}/frameworks/${category}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return result;
    }

    return result;
  },

  /**
   * Reload DataLoader cache on server
   */
  async reloadCache(): Promise<void> {
    const response = await fetch(`${API_BASE}/reload`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`Failed to reload cache: ${response.statusText}`);
    }
  },
};
